import { deepEqual } from 'fast-equals'
import { useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { ClientEditSelect, ColData, UfinetSelect } from 'ufinet-web-components'
import {
	AuthContext,
	ICostsBuildingAccessResponse,
	ICostsEconomicModelResponse,
	ICostsEquipmentResponse,
	ICostsFOResponse,
	ICostsInfrastructureResponse,
	convertToLocalTZ,
	equipmentCostsService,
} from 'ufinet-web-functions'

import { useEquipmentCombinations } from 'pages/costs/EquipmentAndConstruction/hooks/useEquipmentCombinations'
import { useEquipmentRowDraft } from 'pages/costs/EquipmentAndConstruction/hooks/useEquipmentRowDraft'
import { EquipmentRowDraft, getServiceTypeOptions, normalizeToNumber } from './equipmentSelectHelpers'

const isSameOptionValue = (optionValue: any, candidate: any) => {
	if (candidate === null || candidate === undefined) {
		return false
	}

	return optionValue === candidate || String(optionValue) === String(candidate)
}

const syncRowSelection = (rowData: any, baseKey: string, value: any, label?: string) => {
	rowData[`${baseKey}Id`] = value
	rowData[baseKey] = value
	rowData[`${baseKey}Name`] = label
}

type DraftUpdater = (patch: Partial<EquipmentRowDraft>) => void

const useDraftedCombinations = (
	serviceTypeId: number | undefined,
	equipmentService: any,
	draft: EquipmentRowDraft,
	setDraft: DraftUpdater,
	shouldPersist: boolean
) => {
	const { technicalOptions, transmissionOptions, loading } = useEquipmentCombinations(serviceTypeId, equipmentService)

	useEffect(() => {
		if (!shouldPersist) {
			return
		}

		if (!serviceTypeId) {
			if (draft.optionsServiceTypeId !== undefined || draft.technicalOptions || draft.transmissionOptions) {
				setDraft({
					optionsServiceTypeId: undefined,
					technicalOptions: undefined,
					transmissionOptions: undefined,
				})
			}
			return
		}

		if (loading) {
			return
		}

		const shouldUpdate =
			draft.optionsServiceTypeId !== serviceTypeId ||
			!deepEqual(draft.technicalOptions ?? [], technicalOptions) ||
			!deepEqual(draft.transmissionOptions ?? [], transmissionOptions)

		if (shouldUpdate) {
			setDraft({
				optionsServiceTypeId: serviceTypeId,
				technicalOptions,
				transmissionOptions,
			})
		}
	}, [
		shouldPersist,
		serviceTypeId,
		loading,
		technicalOptions,
		transmissionOptions,
		draft.optionsServiceTypeId,
		draft.technicalOptions,
		draft.transmissionOptions,
		setDraft,
	])

	const resolvedTechnicalOptions =
		serviceTypeId && draft.optionsServiceTypeId === serviceTypeId && draft.technicalOptions
			? draft.technicalOptions
			: technicalOptions
	const resolvedTransmissionOptions =
		serviceTypeId && draft.optionsServiceTypeId === serviceTypeId && draft.transmissionOptions
			? draft.transmissionOptions
			: transmissionOptions

	return {
		technicalOptions: resolvedTechnicalOptions,
		transmissionOptions: resolvedTransmissionOptions,
		isTechnicalLoading: loading && resolvedTechnicalOptions.length === 0,
		isTransmissionLoading: loading && resolvedTransmissionOptions.length === 0,
	}
}

// Componente para manejar la lógica del Service Type Select con notificación
const ServiceTypeSelect = ({ rowData, options }: { rowData: any; options: any }) => {
	const intl = useIntl()
	const { draft, setDraft } = useEquipmentRowDraft(rowData, intl)
	const serviceTypeOptions = useMemo(() => getServiceTypeOptions(intl), [intl])

	const currentServiceTypeId = draft.serviceTypeId ?? normalizeToNumber(rowData.serviceType ?? rowData.serviceTypeId)
	const currentServiceTypeName = draft.serviceTypeName ?? rowData.serviceTypeName
	const currentServiceType = serviceTypeOptions.find(
		(opt) => opt.value === currentServiceTypeId || opt.label === currentServiceTypeName
	)

	return (
		<UfinetSelect
			value={currentServiceType || null}
			isClearable
			placeholder={currentServiceType?.label || 'Seleccione tipo de servicio'}
			onChange={(e: any) => {
				if (e && e.value) {
					const numericValue = normalizeToNumber(e.value)
					const sanitizedValue = numericValue ?? e.value
					setDraft({
						serviceTypeId: numericValue,
						serviceTypeName: e.label,
						technicalAttributeId: undefined,
						technicalAttributeName: undefined,
						transmissionMediumId: undefined,
						transmissionMediumName: undefined,
						optionsServiceTypeId: numericValue,
						technicalOptions: undefined,
						transmissionOptions: undefined,
					})

					syncRowSelection(options.rowData, 'serviceType', sanitizedValue, e.label)
					syncRowSelection(options.rowData, 'technicalAttribute', undefined)
					syncRowSelection(options.rowData, 'transmissionMedium', undefined)

					options.editorCallback(e.label)
				} else {
					setDraft({
						serviceTypeId: undefined,
						serviceTypeName: undefined,
						technicalAttributeId: undefined,
						technicalAttributeName: undefined,
						transmissionMediumId: undefined,
						transmissionMediumName: undefined,
						optionsServiceTypeId: undefined,
						technicalOptions: undefined,
						transmissionOptions: undefined,
					})

					syncRowSelection(options.rowData, 'serviceType', undefined)
					syncRowSelection(options.rowData, 'technicalAttribute', undefined)
					syncRowSelection(options.rowData, 'transmissionMedium', undefined)

					options.editorCallback(null)
				}
			}}
			className="w-100"
			options={serviceTypeOptions}
		/>
	)
}

// Componente para manejar la lógica del Technical Attribute Select
const TechnicalAttributeSelect = ({ rowData, options }: { rowData: any; options: any }) => {
	const intl = useIntl()
	const authData = useContext(AuthContext)
	const _equipmentCostsService = useMemo(() => equipmentCostsService(authData, intl), [authData, intl])
	const { draft, setDraft } = useEquipmentRowDraft(rowData, intl)
	const serviceTypeId = draft.serviceTypeId ?? normalizeToNumber(rowData.serviceType ?? rowData.serviceTypeId)
	const { technicalOptions, isTechnicalLoading } = useDraftedCombinations(
		serviceTypeId,
		_equipmentCostsService,
		draft,
		setDraft,
		true
	)
	const currentTechnicalId = draft.technicalAttributeId ?? rowData.technicalAttributeId ?? rowData.technicalAttribute
	const currentTechnicalName = draft.technicalAttributeName ?? rowData.technicalAttributeName
	const currentTechnicalAttribute = technicalOptions.find(
		(opt) => isSameOptionValue(opt.value, currentTechnicalId) || opt.label === currentTechnicalName
	)

	return (
		<UfinetSelect
			value={currentTechnicalAttribute || null}
			isClearable
			placeholder={
				currentTechnicalAttribute?.label || (isTechnicalLoading ? 'Cargando...' : 'Seleccione atributo técnico')
			}
			onChange={(e: any) => {
				if (e && e.value) {
					const sanitizedValue = normalizeToNumber(e.value) ?? e.value
					setDraft({
						technicalAttributeId: sanitizedValue,
						technicalAttributeName: e.label,
					})
					syncRowSelection(options.rowData, 'technicalAttribute', sanitizedValue, e.label)
					options.editorCallback(e.label)
				} else {
					setDraft({
						technicalAttributeId: undefined,
						technicalAttributeName: undefined,
					})
					syncRowSelection(options.rowData, 'technicalAttribute', undefined)
					options.editorCallback(null)
				}
			}}
			className="w-100"
			options={technicalOptions}
			isDisabled={!serviceTypeId || technicalOptions.length === 0 || isTechnicalLoading}
		/>
	)
}

// Componente para manejar la lógica del Transmission Medium Select
const TransmissionMediumSelect = ({ rowData, options }: { rowData: any; options: any }) => {
	const intl = useIntl()
	const authData = useContext(AuthContext)
	const _equipmentCostsService = useMemo(() => equipmentCostsService(authData, intl), [authData, intl])
	const { draft, setDraft } = useEquipmentRowDraft(rowData, intl)
	const serviceTypeId = draft.serviceTypeId ?? normalizeToNumber(rowData.serviceType ?? rowData.serviceTypeId)
	const { transmissionOptions, isTransmissionLoading } = useDraftedCombinations(
		serviceTypeId,
		_equipmentCostsService,
		draft,
		setDraft,
		false
	)
	const currentTransmissionId = draft.transmissionMediumId ?? rowData.transmissionMediumId ?? rowData.transmissionMedium
	const currentTransmissionName = draft.transmissionMediumName ?? rowData.transmissionMediumName
	const currentTransmissionMedium = transmissionOptions.find(
		(opt) => isSameOptionValue(opt.value, currentTransmissionId) || opt.label === currentTransmissionName
	)

	return (
		<UfinetSelect
			value={currentTransmissionMedium || null}
			isClearable
			placeholder={
				currentTransmissionMedium?.label || (isTransmissionLoading ? 'Cargando...' : 'Seleccione medio de transmisión')
			}
			onChange={(e: any) => {
				if (e && e.value) {
					const sanitizedValue = normalizeToNumber(e.value) ?? e.value
					setDraft({
						transmissionMediumId: sanitizedValue,
						transmissionMediumName: e.label,
					})
					syncRowSelection(options.rowData, 'transmissionMedium', sanitizedValue, e.label)
					options.editorCallback(e.label)
				} else {
					setDraft({
						transmissionMediumId: undefined,
						transmissionMediumName: undefined,
					})
					syncRowSelection(options.rowData, 'transmissionMedium', undefined)
					options.editorCallback(null)
				}
			}}
			className="w-100"
			options={transmissionOptions}
			isDisabled={!serviceTypeId || transmissionOptions.length === 0 || isTransmissionLoading}
		/>
	)
}

export const colsFO: ColData[] = [
	{ field: 'countryName', header: 'COST.COUNTRY' },
	{ field: 'networkAreaName', header: 'COST.AREA', width: '250px' },
	{
		field: 'clientId',
		header: 'COST.CLIENT',
		width: '250px',
		editable: true,
		customEditableField: (col: ColData, options: any) => {
			const client: ICostsFOResponse = options.rowData
			return (
				<ClientEditSelect
					noLabel
					unclearable
					placeholder={options.rowData.clientName}
					onChange={(e: any) => {
						!!e && options.editorCallback(e.value)
					}}
					className="w-100"
					clientRequest={{
						countries: [client.countryId],
						corporateGroups: [],
					}}
				/>
			)
		},
		body: (row: ICostsFOResponse) => {
			return <div className="ufinet-table-cell overflow">{row.clientName || '-'}</div>
		},
	},
	{
		field: 'infrastructureType',
		header: 'COST.INFRASTRUCTURETYPE',
		width: '250px',
		editable: false,
	},
	{
		field: 'zoneType',
		header: 'COST.SCOPE',
		width: '250px',
		editable: false,
	},
	{ field: 'minimalDistance', header: 'COST.MINIMALDISTANCE', editable: true, type: 'decimal', nonFilterable: true },
	{ field: 'maximumDistance', header: 'COST.MAXIMUMDISTANCE', editable: true, type: 'decimal', nonFilterable: true },
	{ field: 'opticalFactor', header: 'COST.OPTICALFACTOR', editable: true, type: 'decimal', nonFilterable: true },
	{ field: 'punctualCost', header: 'COST.PUNCTUALCOST', editable: true, type: 'decimal', nonFilterable: true },
	{ field: 'laborPunctualCost', header: 'COST.PUNCTUALCOSTMO', editable: true, type: 'decimal', nonFilterable: true },
	{
		field: 'punctualCostPerMeter',
		header: 'COST.PUNCTUALCOSTPERMETER',
		editable: true,
		type: 'decimal',
		nonFilterable: true,
	},
	{
		field: 'laborPunctualCostPerMeter',
		header: 'COST.PUNCTUALCOSTPERMETERMO',
		editable: true,
		width: '150px',
		type: 'decimal',
		nonFilterable: true,
	},
	{ field: 'lastModifiedBy', header: 'COST.UPDATEDBY', editable: true, width: '250px' },
	{
		field: 'lastModifiedAt',
		header: 'COST.UPDATEDAT',
		editable: true,
		width: '150px',
		nonFilterable: true,
		body: (row: ICostsEconomicModelResponse) => convertToLocalTZ(row.lastModifiedAt),
	},
]

export const colsEquipment: ColData[] = [
	{ field: 'countryName', header: 'COST.COUNTRY' },
	{ field: 'networkAreaName', header: 'COST.AREA', width: '250px' },
	{
		field: 'clientId',
		header: 'COST.CLIENT',
		width: '250px',
		editable: true,
		customEditableField: (col: ColData, options: any) => {
			const client: ICostsEquipmentResponse = options.rowData
			return (
				<ClientEditSelect
					noLabel
					unclearable
					placeholder={options.rowData.clientName}
					onChange={(e: any) => {
						!!e && options.editorCallback(e.value)
					}}
					className="w-100"
					clientRequest={{
						countries: [client.countryId],
						corporateGroups: [],
					}}
				/>
			)
		},
		body: (row: ICostsEquipmentResponse) => {
			return <div className="ufinet-table-cell overflow">{row.clientName || '-'}</div>
		},
	},
	{
		field: 'zoneType',
		header: 'COST.SCOPE',
		editable: false,
		width: '250px',
	},
	{ field: 'minimumCapacity', header: 'COST.MINIMALCAPACITY', editable: true, type: 'decimal', nonFilterable: true },
	{ field: 'maximumCapacity', header: 'COST.MAXIMUMCAPACITY', editable: true, type: 'decimal', nonFilterable: true },
	{ field: 'cost', header: 'COST.COST', editable: true, type: 'decimal', nonFilterable: true },
	{ field: 'laborCost', header: 'COST.COSTMO', editable: true, type: 'decimal', nonFilterable: true },
	{
		field: 'serviceTypeName',
		header: 'COST.SERVICE_TYPE',
		width: '200px',
		editable: true,
		filterType: 'select',
		// Fixed catalog so all values are selectable regardless of what is currently loaded in the table.
		filterOptions: [
			{ label: 'HaaS', value: 'HaaS' },
			{ label: 'LAN Services', value: 'LAN Services' },
			{ label: 'Seguridad', value: 'Seguridad' },
			{ label: 'Venta de equipos', value: 'Venta de equipos' },
			{ label: 'Outsourcing', value: 'Outsourcing' },
			{ label: 'Otros', value: 'Otros' },
			{ label: 'Puntual', value: 'Puntual' },
			{ label: 'VSAT', value: 'VSAT' },
			{ label: 'Equipo', value: 'Equipo' },
			{ label: 'Energía', value: 'Energía' },
			{ label: 'Cross Conexion', value: 'Cross Conexion' },
			{ label: 'FTTH', value: 'FTTH' },
			{ label: 'Towering', value: 'Towering' },
			{ label: 'Cloud', value: 'Cloud' },
			{ label: 'Capacidad', value: 'Capacidad' },
			{ label: 'Internet', value: 'Internet' },
			{ label: 'Fibra oscura', value: 'Fibra oscura' },
			{ label: 'Colocation', value: 'Colocation' },
			{ label: 'Infraestructura', value: 'Infraestructura' },
		],
		customEditableField: (col: ColData, options: any) => {
			return <ServiceTypeSelect rowData={options.rowData} options={options} />
		},
		body: (row: ICostsEquipmentResponse) => (
			<div className="ufinet-table-cell overflow">{(row as any).serviceTypeName || '-'}</div>
		),
	},
	{
		field: 'technicalAttributeName',
		header: 'COST.TECHNICAL_ATTRIBUTE',
		editable: true,
		width: '200px',
		filterType: 'select',
		// Fixed catalog so all values are selectable regardless of what is currently loaded in the table.
		filterOptions: [
			{ label: 'DWDM', value: 'DWDM' },
			{ label: 'TDM', value: 'TDM' },
			{ label: 'Ethernet', value: 'Ethernet' },
			{ label: 'FTTH', value: 'FTTH' },
			{ label: 'DIA', value: 'DIA' },
			{ label: 'BIA', value: 'BIA' },
			{ label: 'IP Transit', value: 'IP Transit' },
			{ label: 'Contenido', value: 'Contenido' },
		],
		customEditableField: (col: ColData, options: any) => {
			return <TechnicalAttributeSelect rowData={options.rowData} options={options} />
		},
		body: (row: ICostsEquipmentResponse) => {
			// Para el body no podemos usar la API, solo mostrar el nombre si está disponible
			return <div className="ufinet-table-cell overflow">{(row as any).technicalAttributeName || '-'}</div>
		},
	},
	{
		field: 'transmissionMediumName',
		header: 'COST.TRANSMISSION_MEDIUM',
		editable: true,
		width: '200px',
		filterType: 'select',
		// Fixed catalog so all values are selectable regardless of what is currently loaded in the table.
		filterOptions: [
			{ label: 'Fibra Óptica', value: 'Fibra Óptica' },
			{ label: 'Inalámbrico', value: 'Inalámbrico' },
			{ label: 'Cobre', value: 'Cobre' },
		],
		customEditableField: (col: ColData, options: any) => {
			return <TransmissionMediumSelect rowData={options.rowData} options={options} />
		},
		body: (row: ICostsEquipmentResponse) => {
			// Para el body no podemos usar la API, solo mostrar el nombre si está disponible
			return <div className="ufinet-table-cell overflow">{(row as any).transmissionMediumName || '-'}</div>
		},
	},
	{ field: 'lastModifiedBy', header: 'COST.UPDATEDBY', width: '250px' },
	{
		field: 'lastModifiedAt',
		header: 'COST.UPDATEDAT',
		width: '155px',
		nonFilterable: true,
		body: (row: ICostsEquipmentResponse) => convertToLocalTZ(row.lastModifiedAt),
	},
]

export const colsBuildingAccess: ColData[] = [
	{ field: 'countryName', header: 'COST.COUNTRY' },
	{ field: 'networkAreaName', header: 'COST.AREA', width: '250px' },
	{
		field: 'clientId',
		header: 'COST.CLIENT',
		width: '250px',
		editable: true,
		customEditableField: (col: ColData, options: any) => {
			const client: ICostsBuildingAccessResponse = options.rowData
			return (
				<ClientEditSelect
					unclearable
					placeholder={options.rowData.clientName}
					onChange={(e: any) => {
						!!e && options.editorCallback(e.value)
					}}
					className="w-100"
					clientRequest={{
						countries: client.countryId ? [client.countryId] : [],
						corporateGroups: [],
					}}
				/>
			)
		},
		body: (row: ICostsBuildingAccessResponse) => {
			return <div className="ufinet-table-cell overflow">{row.clientName || '-'}</div>
		},
	},
	{ field: 'punctualCost', header: 'COST.PUNCTUALCOST', editable: true, type: 'decimal', nonFilterable: true },
	{ field: 'monthlyCost', header: 'COST.MONTHLYCOST', editable: true, type: 'decimal', nonFilterable: true },
	{
		field: 'monthlyCostPerCapacity',
		header: 'COST.PUNCTUALCOSTPERMETER',
		editable: true,
		type: 'decimal',
		nonFilterable: true,
	},
	{
		field: 'ceConnect',
		header: 'COST.CE_CONNECT',
		editable: true,
		width: '150px',
		type: 'decimal',
		nonFilterable: true,
	},
	{
		field: 'distanceFo',
		header: 'COST.DISTANCE_FO',
		editable: true,
		width: '150px',
		type: 'decimal',
		nonFilterable: true,
	},
	{ field: 'lastModifiedBy', header: 'COST.UPDATEDBY', width: '250px' },
	{
		field: 'lastModifiedAt',
		header: 'COST.UPDATEDAT',
		width: '155px',
		nonFilterable: true,
		body: (row: ICostsBuildingAccessResponse) => convertToLocalTZ(row.lastModifiedAt),
	},
]

export const colsInfrastructure: ColData[] = [
	{ field: 'countryName', header: 'COST.COUNTRY' },
	{ field: 'networkAreaName', header: 'COST.AREA', width: '250px' },
	{
		field: 'clientId',
		header: 'COST.CLIENT',
		width: '250px',
		editable: true,
		customEditableField: (col: ColData, options: any) => {
			const client: ICostsInfrastructureResponse = options.rowData
			return (
				<ClientEditSelect
					noLabel
					unclearable
					placeholder={options.rowData.clientName}
					onChange={(e: any) => {
						!!e && options.editorCallback(e.value)
					}}
					className="w-100"
					clientRequest={{
						countries: [client.countryId],
						corporateGroups: [],
					}}
				/>
			)
		},
		body: (row: ICostsInfrastructureResponse) => {
			return <div className="ufinet-table-cell overflow">{row.clientName || '-'}</div>
		},
	},
	{ field: 'infrastructureType', header: 'COST.INFRASTRUCTURETYPE', width: '250px' },
	{
		field: 'monthlyCostPerMeter',
		header: 'COST.MONTHLYCOSTPERMETER',
		width: '150px',
		editable: true,
		type: 'decimal',
		nonFilterable: true,
	},
	{ field: 'punctualCost', header: 'COST.PUNCTUALCOST', editable: true, type: 'decimal', nonFilterable: true },
	{ field: 'lastModifiedBy', header: 'COST.UPDATEDBY', width: '250px' },
	{
		field: 'lastModifiedAt',
		header: 'COST.UPDATEDAT',
		width: '180px',
		nonFilterable: true,
		body: (row: ICostsInfrastructureResponse) => convertToLocalTZ(row.lastModifiedAt),
	},
]

export const colsEconomicModel: ColData[] = [
	{ field: 'countryName', header: 'COST.COUNTRY' },
	{ field: 'networkAreaName', header: 'COST.AREA', width: '250px' },
	{ field: 'clientName', header: 'COST.CLIENT', width: '250px' },
	{
		field: 'investmentMargin',
		header: 'COST.INVESTMARGIN',
		width: '140px',
		editable: true,
		type: 'decimal',
		nonFilterable: true,
	},
	{
		field: 'recurringMargin',
		header: 'COST.RECURRMARGIN',
		width: '150px',
		editable: true,
		type: 'decimal',
		nonFilterable: true,
	},
	{
		field: 'incrementNrc',
		header: 'COST.INCREMENT_NRC',
		width: '120px',
		editable: true,
		type: 'decimal',
		nonFilterable: true,
	},
	{
		field: 'incrementMrc',
		header: 'COST.INCREMENT_MRC',
		width: '120px',
		editable: true,
		type: 'decimal',
		nonFilterable: true,
	},
	{ field: 'lastModifiedBy', header: 'COST.UPDATEDBY', width: '250px' },
	{
		field: 'lastModifiedAt',
		header: 'COST.UPDATEDAT',
		width: '150px',
		nonFilterable: true,
		body: (row: ICostsEconomicModelResponse) => convertToLocalTZ(row.lastModifiedAt),
	},
]
